import IBLogoOSC from '@/assets/images/ib_logo_osc.svg';
import LogoImage from '@/assets/images/logo.png';
import PamojaIcon from '@/assets/images/pamoja_icon.png';
import { RUBRIC_TYPES_WITHOUT_POINTS } from '@/components/ap/rubric/rubricTypes';
import { AppConfig } from '@/config/appConfig';
import { countries } from '@/hooks/dataHelpers/countries';
import { message } from '@/lib/notify/apNotify';
import {
  getAppType,
  getAppVersion,
  getDeviceInfo,
  isNewAppVersion,
} from '@/lib/offline/OfflineAppHelper';
import { useAuthStore } from '@/store/authStore';
import { usePrintStore } from '@/store/printStore';
import {
  CloudLinear,
  DocumentAddLinear,
  GpsLinear,
  LockLinear,
  ShieldCheckLinear,
} from '@assessprep/icons/solar';
import { BookOpen02Line, File06Line } from '@assessprep/icons/untitled';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getDefaultFilter } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { parse } from 'best-effort-json-parser';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { toPng } from 'html-to-image';
import i18n from 'i18next';
import { cloneDeep, forEach, get, isArray, set, sortBy } from 'lodash';
import mudder from 'mudder';
import queryString from 'query-string';
import interval_worker from './intervalWorker';
declare global {
  interface Navigator {
    standalone?: boolean;
  }
}

// R16: native imperative dialog (was the lazy antd UIHelper modal).
const showInfoModal = (props: {
  type?: string;
  title?: string;
  content?: string;
  buttonText?: string;
}): void => {
  void import('@/components/ap/confirm/APDialog').then((m) => m.showInfoModal(props));
};

const translate = i18n.t;

export const MAX_PRINT_LINES_COUNT = 200;

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

interface GetUTCDateOptions {
  daysDiff?: number;
  monthDiff?: number;
  yearDiff?: number;
  type?: 'startOf' | 'endOf';
  format?: string;
}

// type = "startOf", "endOf", daysDiff, format = nil
export const getUTCDate = (obj: GetUTCDateOptions = {}) => {
  const { daysDiff = 0, monthDiff = 0, yearDiff = 0, type, format } = obj;
  dayjs.extend(utc);
  let dateToReturn = dayjs();

  if (type) {
    dateToReturn = dateToReturn[type]('day');
  }
  if (daysDiff) {
    dateToReturn = dateToReturn.add(daysDiff, 'days');
  }
  if (monthDiff) {
    dateToReturn = dateToReturn.add(monthDiff, 'month');
  }
  if (yearDiff) {
    dateToReturn = dateToReturn.add(yearDiff, 'year');
  }

  dateToReturn = dateToReturn.utc();

  if (format) {
    return dateToReturn.format(format);
  }

  // console.log('dateToReturn ==>', dateToReturn, obj);
  return dateToReturn;
};

export const formatJsonApiData = (response: any): any => {
  let finalResponse: any[];

  // We do not access Object.prototype method 'hasOwnProperty' from target object : husky comment
  if (Object.prototype.hasOwnProperty.call(response, 'data')) {
    response = response.data;
  }

  if (Array.isArray(response)) {
    finalResponse = [];
    response.forEach((element) => {
      finalResponse.push(formatJsonApiData(element));
    });
    return finalResponse;
  }

  if (!Object.prototype.hasOwnProperty.call(response, 'attributes')) {
    return response;
  }

  let finalObj = {
    ...response.attributes,
  };

  if (Object.prototype.hasOwnProperty.call(response, 'relationships')) {
    const relationshipsObj: Record<string, any> = {};
    for (const key in response.relationships) {
      if (Object.prototype.hasOwnProperty.call(response.relationships, key)) {
        const element = response.relationships[key];
        if (element.data) {
          const { type, ...finalElemData } = element.data;
          relationshipsObj[key] = { ...finalElemData };
        }
      }
    }

    finalObj = {
      ...finalObj,
      ...relationshipsObj,
    };
  }

  return finalObj;
};

type MediaCallback = (stream: MediaStream) => void;
type ErrorCallback = (error: Error) => void;

type ModalType = 'info' | 'success' | 'error' | 'warning' | 'confirm';

export const getAlphabetAtIndex = (index: number) => {
  const alphabetArr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  return alphabetArr[index];
};
export const loadScript = (url: string, callback: () => void) => {
  const script = document.createElement('script');
  script.src = url;
  script.onload = callback;
  document.body.appendChild(script);
};

export const isImageOrPdfFileType = (file: any) => {
  const supportedContentTypes = ['image', 'pdf'];
  let fileType;
  supportedContentTypes.forEach((ct) => {
    if (
      (file.type && file.type.indexOf(ct) >= 0) ||
      (file.content_type && file.content_type.indexOf(ct) >= 0)
    ) {
      fileType = ct;
    }
  });
  return fileType;
};

export const onDownload = (file: any) => {
  const link = document.createElement('a');
  link.href = file.url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  message.info(translate('CommonText.preview_not_supported_download_the_file_for_access'));
};

export const maxCharacters = 50000;

export const validateText = (text: string, rules: any) => {
  const maxChars = rules.maxCharacters ?? maxCharacters;
  const requiredMessage = rules.requiredMessage ?? translate('CommonText.required');
  const maxCharsMessage =
    rules.maxCharsMessage ?? translate('CommonText.limit_reached_text_will_not_be_saved');
  const validation: { success: boolean; messages: string[] } = {
    success: false,
    messages: [],
  };

  let hasRequiredError, hasLimitError;
  if (rules.required && !text) {
    hasRequiredError = true;
    validation.messages.push(requiredMessage);
  } else if (text && text.length >= maxChars) {
    hasLimitError = true;
    validation.messages.push(maxCharsMessage);
  }

  if (!hasRequiredError && !hasLimitError) {
    validation.success = true;
  }

  return validation;
};

type TimeOffsetResult = {
  success: boolean;
  response?: any;
  error?: any;
};

const isNetworkError = (error: any): boolean => {
  const errorMessage = error?.message?.toLowerCase() || '';
  return (
    errorMessage.includes('network error') ||
    errorMessage.includes('timeout') ||
    error?.code === 'ECONNABORTED'
  );
};

export const fetchServerTimeOffset = async (
  appRegion: string,
  token: string | null,
  timeout = 10000
): Promise<TimeOffsetResult> => {
  if (!token) {
    return { success: false, error: new Error('No token provided') };
  }

  const baseUrl =
    appRegion === 'china' ? import.meta.env.VITE_API_URL : import.meta.env.VITE_AI_SERVICE_URL;
  const serverTimeOffsetURL = baseUrl + '/time_offset?client_time=' + Date.now();

  let config: any = {
    timeout,
    headers: appRegion === 'china' ? { token: token } : { Authorization: `Bearer ${token}` },
  };

  config = updateConfigHeaderForDemoUseStagingAI(config);

  try {
    const response = await axios.get(serverTimeOffsetURL, config);
    return { success: true, response };
  } catch (error) {
    return { success: false, error };
  }
};

export const getServerTimeOffset = (
  callback: (offset: number) => void,
  errorCallback?: () => void
) => {
  const appRegion = import.meta.env.VITE_REGION;
  const token = useAuthStore.getState().token;

  fetchServerTimeOffset(appRegion, token)
    .then((result) => {
      if (result.success) {
        console.log('getServerTimeOffset then', result.response);
        callback(result.response.data.offset);
      } else {
        throw result.error;
      }
    })
    .catch((e) => {
      console.log('getServerTimeOffset catch', e);
      if (isNetworkError(e)) {
        message.error(translate('CommonText.internet_connection_unstable'));
        errorCallback?.();
      } else {
        Sentry.captureException(e);
        message.error('Something went wrong. Please try again.');
      }
    });
};

export const checkOffsetInterval = (callback: any, status = 'on') => {
  const intervalWorker = new Worker(interval_worker);
  intervalWorker.postMessage({ turn: status, interval: 1000 });

  let currentTime = Date.now();
  intervalWorker.onmessage = ({ data: { time } }) => {
    const offset = currentTime - Date.now();
    if (Math.abs(offset) > 120000) {
      // console.log("offset ==>", offset);
      // Note: setting offset greater than 2 minutes, as we are giving margin of error of 2 minutes in currentTimeValidWrtServerTime
      callback(offset);
    }
    currentTime = Date.now();
  };
};

export const supportsMacAACSecurity = (appType: string) => {
  let supportsAAC = false;
  if (appType === 'ios') {
    supportsAAC = true;
  } else if (appType === 'mac') {
    const deviceInfo = getDeviceInfo(appType);
    const currentAppVersion = getAppVersion(appType);
    const deviceOsVersion = deviceInfo.osVersion;
    if (
      isNewAppVersion(currentAppVersion, '10.0.0') &&
      isNewAppVersion(deviceOsVersion, '10.15.4')
    ) {
      supportsAAC = true;
    }
  }
  return supportsAAC;
};

export const updateImagesSRCForCanvas = (canvas_data: any, segment_version: any) => {
  const printMode = usePrintStore.getState().printMode;
  //Always process images to set crossOrigin and avoid tainted canvas errors
  // if (segment_version !== 'old_version' && !printMode) {
  //   return canvas_data;
  // }
  if (!canvas_data) {
    return canvas_data;
  }
  if (!canvas_data.objects) {
    return canvas_data;
  }
  const objects = canvas_data.objects;
  objects.map((object: any) => {
    if (object.type == 'image') {
      object.src = object.src ?? object.local_src_backup;
      // Always set crossOrigin to avoid tainted canvas errors (for print mode, AI grading, etc.)
      object.crossOrigin = 'anonymous';
      if (printMode) {
        // Ensure we bust cache for print mode so images reload (avoid tainted canvas)
        const cacheBuster = (object.src.includes('?') ? '&' : '?') + 'timestamp=' + Date.now();
        object.src = object.src + cacheBuster;
      }
    }
  });
  return canvas_data;
};
export const showConvertedAchievementLevel = (experience: any) => {
  let showConvertedAchievementLevel = false;
  if (experience?.rubric?.type_c === 'criteria_with_points') {
    showConvertedAchievementLevel = true;
  }
  return showConvertedAchievementLevel;
};

interface Strand {
  label: string;
  title?: string;
  desc?: string;
  level_descriptors?: any;
  grade_code?: string;
}

const processStrands = (criteria: any, criterium_associations: any, relation_items: any) => {
  const criteriaStrands = criteria.strands ?? [];
  const finalStrands: Strand[] = [];

  criterium_associations?.forEach((ca: any) => {
    let currentGradeStrands: any[] = [];
    if (relation_items.length > 0 && criteriaStrands.length > 0) {
      currentGradeStrands = criteriaStrands.filter(
        (s: any) => s.grade_code === relation_items[0].grade_code
      );
    }

    if (currentGradeStrands.length > 0 && currentGradeStrands[0].strands) {
      currentGradeStrands[0].strands.forEach((cgs: any) => {
        ca.strands.length &&
          ca.strands.forEach((cas: any) => {
            if (cas.label === cgs.label) {
              finalStrands.push(cgs);
            }
          });
      });
    }
  });

  return finalStrands;
};

export const mapStrandLabelsToLevelDescriptors = (aiStrands: any) => {
  // Iterate through each criteria in aiStrands
  const strandLevelDescriptorsMap: any = {};
  forEach(aiStrands, (strand: any) => {
    const newLevelDescriptors: any[] = [];
    const levelDescriptor = isArray(strand.level_descriptors)
      ? strand.level_descriptors[0]
      : strand.level_descriptors;
    forEach(levelDescriptor, (desc, level) => {
      const levels = level.split('-');

      // can use range if levels come in more then difference of 1 like 2-4 and not 3-4
      // levels = range(levels[0], levels[1] ? levels[1] + 1 : levels[0] + 1)

      forEach(levels, (level) => {
        newLevelDescriptors.push({
          desc,
          level: parseInt(level),
        });
      });
    });

    const normalizedLabel = (strand.strand_label || '').replace(/^Strand\s*=\s*/i, '').trim();
    strandLevelDescriptorsMap[normalizedLabel] = newLevelDescriptors;
  });

  return strandLevelDescriptorsMap;
};

export const getStrandsFromRubricsWithUpdatedCA = (
  criterium_associations: any,
  criteria: any,
  relation_items: any,
  aiGeneratedLevelDescriptors?: any
) => {
  const newFinalStrands: Strand[] = [];
  const newCriteriumAssociations = cloneDeep(criterium_associations);
  criteria?.forEach((criteria: any) => {
    const finalStrands = processStrands(criteria, criterium_associations, relation_items);
    newFinalStrands.push(...finalStrands);
  });
  let strandLevelDescriptorsMap: any = {};
  if (aiGeneratedLevelDescriptors) {
    strandLevelDescriptorsMap = mapStrandLabelsToLevelDescriptors(aiGeneratedLevelDescriptors);
  }

  newCriteriumAssociations?.forEach((ca: any) => {
    ca.strands.length &&
      ca.strands.forEach((s: any) => {
        newFinalStrands.forEach((fs) => {
          if (s.label === fs.label) {
            s.level_descriptors = strandLevelDescriptorsMap[fs.label] ?? fs.level_descriptors;
            s.title = fs.title;
            s.desc = fs.desc;
          }
        });
      });
  });

  return newCriteriumAssociations;
};

export const getQBDesc = (code: string, translate: any) => {
  const qbDescMap: any = {
    school: translate('HelperMethods.all_assessments_authored_by_teachers'),
    ib_dp: translate('HelperMethods.past_ib_exams'),
    osc_dp: translate('HelperMethods.realistic_full_exams_created_by_pamoja'),
    ap_myp: translate('HelperMethods.mock_eassessments_and_topic_wise_formatives'),
    ap_myp_standard: translate('HelperMethods.mock_eassessments_and_topic_wise_formatives'),
    ap_dp: translate('HelperMethods.realistic_full_exams_created_by_assessprep_and_pamoja'),
  };
  return qbDescMap[code];
};

export const checkURLSource = (url: string) => {
  if (url) {
    if (url.match(/(https:\/\/\S*vimeo\S*)\w+/g)) {
      return 'vimeo';
    } else if (url.match(/(https:\/\/\S*youtu\S*)\w+/g)) {
      return 'yt';
    } else if (url.match(/(https:\/\/\S*youku\S*)\w+/g)) {
      return 'youku';
    } else if (url.match(/direct_uploads\/attachments/g)) {
      return 's3';
    } else {
      return '';
    }
  } else {
    return '';
  }
};

// Anchored, variant-complete YouTube video-id extractor (distinct from the looser
// getYouTubeId in pages/announcements/utils.ts). Covers watch / embed / shorts / live / v
// paths on youtu.be and www/m/music/nocookie hosts; anchoring (^) rejects spoofed hosts
// (e.g. evil.com?ref=youtube.com/...). Private — only the privacy-enhance rewrite needs it.
const extractYouTubeVideoId = (url: string): string => {
  const match = url?.match(
    /^(?:https?:)?\/\/(?:www\.|m\.|music\.)?(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?.*?\bv=|embed\/|shorts\/|live\/|v\/))([\w-]{11})/i
  );
  return match?.[1] ?? '';
};

// Rewrites a YouTube URL to its Privacy-Enhanced (youtube-nocookie.com) form so the
// embed sets no tracking cookies before playback (SIVON/DPO requirement, APL-11703).
// Every variant funnels through /embed/<id>; non-YouTube and already-nocookie URLs pass through.
export const toYouTubePrivacyEnhancedUrl = (url: string): string => {
  if (!url || /youtube-nocookie\.com/i.test(url)) return url;
  const id = extractYouTubeVideoId(url);
  if (!id) return url; // playlist-only/channel URLs: host-swap just redirects back — leave as-is

  const params = new URLSearchParams(url.split('?')[1] ?? '');
  params.delete('v');
  const t = params.get('t');
  if (t) {
    params.delete('t');
    const s = parseInt(t);
    if (!isNaN(s)) params.set('start', String(s)); // embed player ignores `t`
  }
  const qs = params.toString();
  return `https://www.youtube-nocookie.com/embed/${id}${qs ? `?${qs}` : ''}`;
};

export const convertTimetoObject = (time: any) => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return { hours, minutes, seconds };
};

export const getUniqueId = (len = 16, type?: 'alphabet_only' | 'numbers_only') => {
  let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  if (type === 'alphabet_only') {
    possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  } else if (type === 'numbers_only') {
    possible = '0123456789';
  }
  let objectId = '';
  let i = 0;

  while (i < len) {
    objectId += possible.charAt(Math.floor(Math.random() * possible.length));
    i++;
  }

  return objectId;
};

export const updateConfigHeaderForDemoUseStagingAI = (config: any) => {
  if (import.meta.env.VITE_MODE === 'demo' || import.meta.env.VITE_MODE === 'demo-internal') {
    config.headers['X-ENV'] = 'demo';
  } else if (import.meta.env.VITE_MODE === 'staging' && import.meta.env.VITE_REGION === 'china') {
    config.headers['X-ENV'] = 'china_staging';
  } else if (
    import.meta.env.VITE_MODE === 'production' &&
    import.meta.env.VITE_REGION === 'china'
  ) {
    config.headers['X-ENV'] = 'china_production';
  }
  // else if (import.meta.env.VITE_MODE === "development") {
  //   config.headers["X-ENV"] = "development"
  // }

  return config;
};

export const getMinutes = (seconds: any) => {
  // console.log('seconds==>', seconds);
  const minutes = parseInt((Number(seconds) / 60).toString());

  if (minutes < 10) {
    return '0' + minutes.toString();
  }

  return minutes;
};

export const getSeconds = (seconds: any) => {
  // console.log('seconds==>', seconds);
  const secs = parseInt((Number(seconds) % 60).toString());

  if (secs < 10) {
    return '0' + secs.toString();
  }

  return secs;
};

export const checkProcessingProgress = (props: any) => {
  console.log('checkProcessingProgress===>', props);
  const { job_id, interval, successCallback, errorCallback, intervalRef } = props;
  const token = useAuthStore.getState().token;
  console.log('aq token', token);

  const config = {
    headers: {
      'x-access-token': token,
    },
  };

  if (job_id) {
    const url = `${import.meta.env.VITE_NODE_SERVICE_BASE_URL}api/auth/job/${job_id}`;
    console.log('aq url', url);
    let checkProgressInterval: any = null;
    const getRequest = () => {
      console.log('aq get request running');

      axios
        .get(url, config)
        .then((value) => {
          console.log('axios checking progress ==>', value);
          successCallback(value.data, checkProgressInterval);

          if (value.data.progress === 100) {
            clearInterval(checkProgressInterval);
          }
        })
        .catch((error) => {
          console.log('axios checking progress error ==>', error);
          errorCallback(error, checkProgressInterval);
        });
    };

    getRequest();
    checkProgressInterval = setInterval(getRequest, interval ?? 30000);
    intervalRef.current = checkProgressInterval;
  }
};

export function fetchTimeoutWrapper(ms: number, promise: Promise<any>) {
  return new Promise(function (resolve, reject) {
    setTimeout(function () {
      reject(new Error('timeout'));
    }, ms);
    promise.then(resolve, reject);
  });
}

export const getOKPingUrl = (appRegion: string) => {
  let url = 'https://v2res1.assessprep.com/manual_uploads/OK.txt';
  // let url = "https://v2res1-stag.assessprep.com/manual_uploads/OK.txt"
  // let url = "https://v2res1-stag.assessprep.com/manual_uploads/1px.png"
  if (appRegion === 'china') {
    url = 'https://production-eks-assets.assessprep.cn/manual_uploads/OK.txt';
  }
  return url;
};

export const checkURLExist = (props: any, checkProgressInterval: any) => {
  const { url, interval, successCallback, errorCallback } = props;
  const token = useAuthStore.getState().token;

  const config = {
    headers: {
      token: token,
    },
  };

  let checkURLInterval: any = null;

  const headRequest = () => {
    axios
      .head(url, config)
      .then((value) => {
        console.log('axios checking if url exists success ==>', value);
        clearInterval(checkURLInterval);
        clearInterval(checkProgressInterval);
        successCallback(value);
      })
      .catch((error) => {
        console.log('axios checking if url exists error ==>', error);
        clearInterval(checkURLInterval);
        errorCallback(error);
      });
  };

  headRequest();
  checkURLInterval = setInterval(headRequest, interval ?? 30000);
};

export const findStandardsByGrade = (
  data: any,
  gradeLabel?: string | null,
  gradeNumber?: string | null,
  gradeType?: string
) => {
  if (!(gradeNumber || gradeLabel)) {
    return data;
  }
  let findBy = 'grade_number';
  if (!gradeNumber && gradeLabel) {
    findBy = 'grade_label';
  }

  const findRelatedItems = (item: any, relatedItems: any) => {
    if (!item) return;
    relatedItems.push(item);

    const parent = data.find((parentItem: any) => parentItem.id === item.parent_id);
    if (parent && !relatedItems.some((relatedItem: any) => relatedItem.id === parent.id)) {
      findRelatedItems(parent, relatedItems);
    }
  };

  // Filter standards first
  const standards = data.filter(
    (item: any) =>
      // item.type_c === "standard" &&
      item.type_c === 'topic' &&
      !item.has_children &&
      (item?.relations?.some(
        (relation: any) =>
          ((findBy === 'grade_number' && relation.grade_number === gradeNumber) ||
            (findBy === 'grade_label' && relation.label === gradeLabel)) &&
          relation.type_c === gradeType
      ) ||
        !item.relations ||
        item.relations.length === 0)
  );

  const relatedItems: any[] = [];

  standards.forEach((standard: any) => findRelatedItems(standard, relatedItems));

  return relatedItems;
};
interface GetKeysOptions {
  parseInt?: boolean;
  value?: boolean;
}

export const getKeysFromObject = <T extends Record<string, any>>(
  object: T,
  options: GetKeysOptions = {}
): Array<string | number | T[keyof T]> => {
  const finalArr: Array<string | number | T[keyof T]> = [];

  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      const element = object[key];
      let finalKey: string | number = key;

      if (options.parseInt) {
        finalKey = parseInt(key);
      }

      if (element !== false) {
        if (options.value) {
          finalArr.push(element);
        } else {
          finalArr.push(finalKey);
        }
      }
    }
  }

  return finalArr;
};

export const sortListByPosition = (list: any) => {
  if (!list?.length) return list;

  const sortedList = sortBy(list, 'position');

  sortedList.forEach((item: any) => {
    if (item.children?.length) {
      item.children = sortListByPosition(item.children);
    }
  });

  return sortedList;
};

export const convertToNestedList = (flattenedArray: any) => {
  const nestedStructures: any[] = [];
  const nodeMap: any = {};

  flattenedArray.forEach((node: any) => {
    nodeMap[node.id] = { ...node, children: [] };
  });

  flattenedArray.forEach((node: any) => {
    if (node.parent_id && nodeMap[node.parent_id]) {
      nodeMap[node.parent_id].children.push(nodeMap[node.id]);
    } else {
      nestedStructures.push(nodeMap[node.id]);
    }
  });

  return nestedStructures;
};

export const allowedPasteCharactersLimit = 100;
export const experienceMaxEngagementPoints = (experience: any, experienceConfig: any) => {
  const experienceConfigTake = experienceConfig?.takeExperience;

  if (!experience) {
    return 0;
  }
  const engagementConfig = experienceConfigTake?.engagement;

  console.log('experienceMaxEngagementPointsSelector', engagementConfig, experience);

  const segmentTypesCount = experience?.custom_fields?.segment_types_count;
  let maxEngagementPoints = 0;

  if (segmentTypesCount) {
    if (engagementConfig?.video?.enabled) {
      maxEngagementPoints += segmentTypesCount.video * engagementConfig.video.points;
    }
    if (engagementConfig?.question?.enabled) {
      maxEngagementPoints += segmentTypesCount.question * engagementConfig.question.points;
    }
    if (engagementConfig?.game?.enabled) {
      maxEngagementPoints += segmentTypesCount.game * engagementConfig.game.points;
    }
  }

  return maxEngagementPoints;
};

// Helper functions
export const getSessionId = (len = 4) => {
  return `${Date.now()}-${getUniqueId(len)}`;
};

export const replaceAndExtractAltTextFromImage = (text: string) => {
  const srcRegex = /\ssrc\s*=\s*(['"])(.*?)\1\sdata-mathml=\s*(['"])(.*?)\3/g;
  const altRegex = /<img [^>]*alt="([^"]+)"[^>]*>/g;

  let cleanText = text.replace(srcRegex, '');
  cleanText = cleanText.replace(altRegex, (match: any, altText: any) => altText);

  return cleanText.replace(/space/g, '');
};

export const getSelectedHtml = () => {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const serializer = new XMLSerializer();
    let html = '';

    // Loop through nodes in the selection and serialize to HTML
    range.cloneContents().childNodes.forEach((node) => {
      const serializedNode = serializer.serializeToString(node);
      html += serializedNode;
    });
    // Remove xmlns attribute from <span> elements because TTS do not interpret them as XML namespaces
    html = html.replace(/xmlns="[^"]*"/g, '');
    return replaceAndExtractAltTextFromImage(html);
  }

  return '';
};

export const isValidHttpsUrl = (url: string) => {
  if (!url) {
    return false;
  }
  const matcher = /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|localhost[:?\d]*)\S*$/;
  const isValidHttpsUrl = matcher.test(url);
  return isValidHttpsUrl;
};

export const isValidZoomOrMeetUrl = (url: string) => {
  const zoomRegExp = new RegExp(getRegexExpFor('zoom_or_meet'));
  return zoomRegExp.test(url);
};

export const isValidZoomUrl = (url: string) => {
  const zoomRegExp = new RegExp(getRegexExpFor('zoom'));
  return zoomRegExp.test(url);
};

export const getRegexExpFor = (key: string) => {
  const regexExpressionsMap: any = {
    zoom: '^https://((zoom.(com|cn|us))|(.*?.zoom.(com|cn|us)))',
    zoom_or_meet: '^https://((zoom.(com|cn|us))|(.*?.zoom.(com|cn|us))|(meet.(google).(com|cn)))',
  };
  return regexExpressionsMap[key] ?? '';
};

export const getZoomMeetingId = (url: string) => {
  const defaultZoomRegex = new RegExp('^https://.*.zoom.us/.*/.*');
  let meetingId: string | false = '';
  if (defaultZoomRegex.test(url)) {
    const regexValue = url.match(/wc.*|j.*/g);
    const finalRegex = regexValue?.[0]?.replace(/join|wc|\/|j|\?.*/g, '');
    meetingId = finalRegex ?? false;
  }
  console.log('meeting id===>', meetingId);
  return meetingId;
};

export const getZoomJoinUrlForEmbedded = (url: string) => {
  let zoomJoinUrl = url;
  const meetingId = getZoomMeetingId(url);
  //uncomment code if password needed
  //APL-1744 ref url : https://support.digiexam.se/hc/en-us/articles/360017885500-Zoom-meeting-and-remote-exams
  //const queryParam = new URL(url).searchParams.get('pwd');
  //const joinWithPassword = (queryParam)? "?pwd="+queryParam+"/join" : "/join"
  if (meetingId) {
    // zoomJoinUrl = "https://us04web.zoom.us/wc/"+`${meetingId}`+ "/join"
    zoomJoinUrl = 'https://us04web.zoom.us/wc/join/' + `${meetingId}`; // zoom has changed url structure
  }
  return zoomJoinUrl;
};

export function calculatePercentage(numerator: any, denominator: any) {
  if (!denominator || !parseFloat(denominator)) {
    return 0;
  }
  return (numerator * 100) / denominator;
}

export const hasSyllabusItemsInSegments = (segments: any) => {
  if (!Array.isArray(segments)) {
    return false;
  }

  if (!segments.length) return false;

  const segment = segments.find(
    (segment: any) => segment.relation_items?.[0]?.syllabus_items?.length > 0
  );
  if (segment) {
    return true;
  }
  return false;
};

export const getIpClient = async () => {
  try {
    const response = await axios.get('https://api.ipify.org?format=json');
    // console.log('inside helper checking==>', response.data.ip);
    return response.data.ip;
  } catch (error) {
    console.error(error);
  }
};

export { deleteFromLS, getFromLS, setToLS } from './lsHelpers';

export const formatNumberWithCommas = (number: number) => {
  return number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatDate = (inputDate: any) => {
  const parsedDate = dayjs(inputDate);
  return parsedDate?.format('DD/MM/YYYY');
};

export const allowedDateFormats = [
  'DD-MM-YYYY',
  'MM-DD-YYYY',
  'DD-MM-YY',
  'MM-DD-YY',
  'DD.MM.YY',
  'DD/MM/YY',
  'YYYY-MM-DD',
  'DD/MM/YYYY',
  'MM/DD/YYYY',
  'YYYY/MM/DD',
  'DD.MM.YYYY',
  'MM.DD.YYYY',
  'YYYY.MM.DD',
  'DD-MMM-YYYY',
  'MMM-DD-YYYY',
  'YYYY-MMM-DD',
  'DD MMM YYYY',
  'MMM DD, YYYY',
  'YYYY MMM DD',
  'DD-MMMM-YYYY',
  'MMMM-DD-YYYY',
  'YYYY-MMMM-DD',
  'DD MMMM YYYY',
  'MMMM DD, YYYY',
  'YYYY MMMM DD',
];

export const cleanObject = (obj: any) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === 'object') cleanObject(obj[key]);
    else if (obj[key] === undefined) delete obj[key];
  });
};

export const toTitleCase = (input: string | null | undefined) => {
  if (input !== null && input !== undefined) {
    return input
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
};

export const getAvatarIcon = (name: string) => {
  return name?.charAt(0).toUpperCase();
};

export const copyToClipboard = (text: string): void => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      message.success(translate('CommonText.log_copied'));
    })
    .catch((err) => {
      message.error(translate('CommonText.failed_to_copy_log'));
    });
};

export const getSubjectIconMap = (): Record<string, string> => {
  const subjectIconMap = {
    biology: 'Biology.png',
    chemistry: 'Chemistry.png',
    physics: 'Physics.png',
    sciences: 'Sciences.png',
    mathematics: 'Mathematics.png',
    'mathematics: analysis and approaches': 'Mathematics A&I.png',
    'mathematics: applications and interpretation': 'Mathematics A&A.png',
    'standard mathematics': 'Standard-mathematics.png',
    'extended mathematics': 'Extended-mathematics.png',
    economics: 'Economics.png',
    geography: 'Geography.png',
    history: 'History.png',
    'integrated humanities': 'Integrated-humanities.png',
    'business management': 'Business-&-Management.png',
    english: 'English A Language & Literature.png',
    french: 'French A Language & Literature.png',
    dance: 'Dance.png',
    'computer science': 'Computer Science.png',
    psychology: 'Psychology.png',
    'social and cultural anthropology': 'Social And Cultural Anthropology.png',
    'world religions': 'World Religions.png',
  };

  return subjectIconMap;
};

export const experienceTypeOptions = (translate: any) => {
  return [
    {
      label: translate('ExperienceCreateForm.assessment'),
      value: 'assessment',
      icon: File06Line,
    },
    {
      label: translate('ExperienceCreateForm.learning'),
      value: 'learning',
      disabled: true,
      icon: BookOpen02Line,
    },
    {
      label: translate('CommonText.group'),
      value: 'group',
      icon: GpsLinear,
    },
  ];
};

// export const writeToClipboardPolyfill = async (str: string, html = false) => {
//   return new Promise<void>((resolve, reject) => {
//     let success = false;
//     // For the clipboard API part, use .then() syntax instead of await:
//     if (
//       navigator.clipboard &&
//       navigator.clipboard.writeText &&
//       navigator.clipboard.write &&
//       success === false
//     ) {
//       if (html) {
//         const item = new window.ClipboardItem({
//           ['text/html']: new Blob([str], { type: 'text/html' }),
//         });
//         navigator.clipboard
//           .write([item])
//           .then(() => {
//             success = true;
//             resolve();
//           })
//           .catch(() => reject(new Error(translate('CommonText.failed_to_copy'))));
//       } else {
//         navigator.clipboard
//           .writeText(str)
//           .then(() => {
//             success = true;
//             resolve();
//           })
//           .catch(() => reject(new Error(translate('CommonText.failed_to_copy'))));
//       }
//       return; // Return early to prevent reaching the code below
//     }

//     // Rest of your success checking code
//     if (!success) {
//       if (str.length > 0) {
//         message.error(translate('CommonText.failed_to_copy'));
//       }
//       reject(new Error(translate('CommonText.failed_to_copy')));
//     } else {
//       resolve();
//     }
//   });
// };

export const writeToClipboardPolyfill = async (str: string, html = false) => {
  console.log('writeToClipboardPolyfill called', str, html);
  let success = false;
  let lastError: Error | null = null;

  const appType = getAppType();
  const currentAppVersion = getAppVersion(appType);
  // For Mac app version 11.0.0 and above, private clipboard is enabled.
  // On the native side, the clipboard is cleared when the "Join Test" button is clicked.
  // TODO: Support a custom copy-to-clipboard function on the native side.
  if (appType === 'mac' && isNewAppVersion(currentAppVersion, '12.1.0')) {
    return null;
  }

  // Try modern Clipboard API
  if (navigator.clipboard && (html ? navigator.clipboard.write : navigator.clipboard.writeText)) {
    try {
      if (html) {
        const item = new window.ClipboardItem({
          'text/html': new Blob([str], { type: 'text/html' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(str);
      }
      console.log('Copied via Clipboard API');
      success = true;
    } catch (e) {
      // iPad Safari blocks clipboard.writeText('') with NotAllowedError for empty strings.
      // Retry with a space character to clear meaningful clipboard content.
      if (e instanceof DOMException && e.name === 'NotAllowedError' && str.length === 0) {
        try {
          await navigator.clipboard.writeText(' ');
          console.log('Copied via Clipboard API (space fallback)');
          success = true;
        } catch (retryErr) {
          console.warn('Clipboard API space fallback failed', retryErr);
          lastError = retryErr instanceof Error ? retryErr : new Error(String(retryErr));
        }
      } else {
        console.warn('Clipboard API copy failed', e);
        lastError = e instanceof Error ? e : new Error(String(e));
      }
    }
  }

  // Try fallback execCommand if modern API failed
  if (!success) {
    try {
      const listener = (e: any) => {
        const format = html ? 'text/html' : 'text/plain';
        e.clipboardData.setData(format, str);
        e.preventDefault();
        success = true;
      };

      document.addEventListener('copy', listener);
      const copySuccess = document.execCommand('copy');
      document.removeEventListener('copy', listener);

      if (copySuccess) {
        console.log('Fallback copy successful');
        success = true;
      }
    } catch (e) {
      console.error('Fallback copy method failed', e);
      lastError = e instanceof Error ? e : new Error(String(e));
    }
  }

  // Handle final result
  if (success) {
    return;
  } else {
    const errorMessage = str.length > 0 ? 'Copy to clipboard failed' : 'Failed to clear clipboard';

    if (AppConfig.mode === 'staging') {
      message.error(errorMessage);
    }
    const error = new Error(errorMessage);

    // Send error to Sentry with context
    Sentry.captureException(error, {
      contexts: {
        clipboard: {
          contentLength: str.length,
          isHtml: html,
          hasClipboardAPI: !!navigator.clipboard,
          hasClipboardWrite: !!navigator.clipboard?.write,
          hasClipboardWriteText: !!navigator.clipboard?.writeText,
        },
      },
      extra: {
        originalError: lastError?.message,
        contentPreview: str.substring(0, 100),
      },
    });

    if (str.length > 0) {
      message.error('Failed to copy');
    }
    throw error;
  }
};

export const checkMob = () => {
  let check = false;

  if (import.meta.env.VITE_MODE == 'production') {
    (function (a) {
      if (
        // /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|playbook|silk/i.test(
          a
        ) ||
        // eslint-disable-next-line
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || (window as any)?.opera);

    check = check || window.matchMedia('(max-width: 768px)').matches;
    // Added matchMedia because checkMob wsn't triggering inside iframe in MB.
  } else {
    if (window.matchMedia) {
      check = window.matchMedia('(max-width: 600px)').matches;
    }
  }
  // console.log('checking mob', check);

  return check;
};

export const convertToHoursAndMinutes = (minutes = 0) => {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0 ? `${hours}h` : `${hours}h ${remainingMinutes}m`;
};
export const isKeyCodeValid = (str: string) => {
  return str && /^[a-z0-9_]+$/.test(str);
};

export const extraTimeValues = [
  // "0",
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '12',
  '15',
  '20',
  '30',
  '40',
  '50',
  '60',
  '70',
  '80',
  '90',
  '100',
  '110',
  '120',
];

export const convertMs = (time: number | string): number => {
  // console.log('time==>', time);
  const ms = Number(time) * 60 * 1000;
  return ms;
};

export const buildCountriesOptionsArr = () => {
  const options = countries.countries;
  const optionsArr: any[] = [];
  options.forEach((value) => {
    optionsArr.push({ label: value.name, value: value.code });
  });

  return optionsArr;
};

export const buildOptionsArr = (options: any) => {
  const optionsArr: any[] = [];
  options.map((value: any, index: any) => {
    optionsArr.push({ label: value, value: value });
  });

  return optionsArr;
};

export const displayPoints = (points: string, truncated = false, cleaned = true) => {
  if (points === null || points === undefined) {
    // console.log("displayPoints - return empty", points)
    return '';
  }
  let pts = parseFloat(points).toFixed(1);
  // Truncate not needed anymore
  // if(truncated && import.meta.env.VITE_MODE === "production"){
  //   // pts = parseFloat(points).toFixed(0)
  //   pts = Math.trunc(parseFloat(points))
  // }
  if (cleaned) {
    const splitPts = pts.split('.');
    // console.log('displayPoints cleaned', cleaned, pts, splitPts);
    if (splitPts.length > 1 && splitPts[1] && parseInt(splitPts[1]) === 0) {
      pts = splitPts[0];
    }
  }
  return pts;
};

export const getBrowserVersion = (
  userAgent: string,
  regex: RegExp,
  removeExtraInfo?: boolean | null,
  index?: number
) => {
  if (removeExtraInfo) {
    const browserVersion = regex.exec(userAgent)?.[0];
    return browserVersion ? browserVersion.replace(/Version\//g, '') : '';
  }
  return regex.exec(userAgent)?.[index ?? 2] ?? null;
};

export const getBrowser = () => {
  const userAgent = navigator.userAgent;

  const brave = (navigator as any)?.userAgentData?.brands?.find(
    (brand: any) => brand?.brand?.toLowerCase() === 'brave'
  );
  const braveBrand = brave ? brave.brand : null;
  const braveVersion = brave ? brave.version : null;

  let browser = 'unknown';
  // Detect browser name
  browser = /ucbrowser/i.test(userAgent) ? 'UCBrowser' : browser;
  browser = /edg/i.test(userAgent) ? 'Edge' : browser;
  browser = /googlebot/i.test(userAgent) ? 'GoogleBot' : browser;
  browser = /chromium/i.test(userAgent) ? 'Chromium' : browser;
  browser = /firefox|fxios/i.test(userAgent) && !/seamonkey/i.test(userAgent) ? 'Firefox' : browser;
  browser = /; msie|trident/i.test(userAgent) && !/ucbrowser/i.test(userAgent) ? 'IE' : browser;
  browser =
    /chrome|crios/i.test(userAgent) &&
    !/opr|opera|chromium|edg|ucbrowser|googlebot/i.test(userAgent)
      ? 'Chrome'
      : browser;
  browser =
    /safari/i.test(userAgent) &&
    !/chromium|edg|ucbrowser|chrome|crios|opr|opera|fxios|firefox/i.test(userAgent)
      ? 'Safari'
      : browser;
  browser = /opr|opera/i.test(userAgent) ? 'Opera' : browser;
  browser = braveBrand ? 'Brave' : browser;

  // detect browser version
  switch (browser) {
    case 'UCBrowser':
      return `${browser}/${getBrowserVersion(userAgent, /(ucbrowser)\/([\d.]+)/i)}`;
    case 'Edge':
      return `${browser}/${getBrowserVersion(userAgent, /(edge|edga|edgios|edg)\/([\d.]+)/i)}`;
    case 'GoogleBot':
      return `${browser}/${getBrowserVersion(userAgent, /(googlebot)\/([\d.]+)/i)}`;
    case 'Chromium':
      return `${browser}/${getBrowserVersion(userAgent, /(chromium)\/([\d.]+)/i)}`;
    case 'Firefox':
      return `${browser}/${getBrowserVersion(userAgent, /(firefox|fxios)\/([\d.]+)/i)}`;
    case 'Chrome':
      return `${browser}/${getBrowserVersion(userAgent, /(chrome|crios)\/([\d.]+)/i)}`;
    case 'Safari':
      return `${browser}/${getBrowserVersion(userAgent, /(Version)\/\d+([^\s]+)/gi, true)}`;
    case 'Opera':
      return `${browser}/${getBrowserVersion(userAgent, /(opera|opr)\/([\d.]+)/i)}`;
    case 'Brave':
      return `${browser}/${braveVersion}`;
    case 'IE': {
      const version = getBrowserVersion(userAgent, /(trident)\/([\d.]+)/i);
      // IE version is mapped using trident version
      // IE/8.0 = Trident/4.0, IE/9.0 = Trident/5.0
      return version ? `${browser}/${parseFloat(version) + 4.0}` : `${browser}/7.0`;
    }
    default:
      return `unknown/0.0.0.0`;
  }
};

export const getBrowserName = (browser?: string) => {
  let browserName = '';
  if (browser) {
    browserName = browser.match(/^(\w+)/g)?.[0] ?? '';
  } else {
    browserName = getBrowser().match(/^(\w+)/g)?.[0] ?? '';
  }

  return browserName;
};

export const getMobileOperatingSystem = () => {
  const userAgent: any = navigator.userAgent || navigator.vendor || window.opera;

  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  }

  if (/android/i.test(userAgent)) {
    return 'Android';
  }

  if (/iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'IOS';
  }
  // IPad Browser no longer has userAgent IOS - instead is now Macintosh - so handling that to identify iPad
  if (checkIPAD()) {
    return 'IPad OS';
  }

  return 'unknown';
};

export const getOS = (): string | null => {
  const platform = window.navigator.platform,
    macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
    windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  let os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'Mac OS';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows';
  } else if (!os && /Linux/.test(platform)) {
    const userAgent = navigator.userAgent || window.navigator.userAgent;
    if (userAgent && userAgent.indexOf('CrOS') !== -1) {
      os = 'ChromeOS';
    } else {
      os = 'Linux';
    }
  }

  // return "Windows"
  return os;
};

export const openUrl = (url: string) => {
  if (url) {
    window.location.href = url;
  }
};

/**
 * Returns true if `url` resolves to the same origin as `expectedOrigin`
 * (defaults to the current page — pass `AppConfig.webUrl` when validating
 * a URL built off that config rather than off wherever this page instance
 * happens to be served from, e.g. local dev / PR-session origins diverge
 * from `VITE_WEB_URL`).
 * - Relative paths (e.g. `/assessments/abc`) → same-origin (true)
 * - Absolute same-host URLs (e.g. `https://app.assessprep.com/foo`) → true
 * - Cross-origin URLs (e.g. `https://evil.com/`) → false
 * - Custom schemes (`assesspass://`, `javascript:`, `data:`) → false
 *   (their parsed origin is `null` or unique, never matching ours)
 * - Unparseable strings → false
 *
 * Used as an open-redirect guard before `window.location.href = url`
 * when `url` came from a query string / option / network response.
 */
export const isSameOriginUrl = (
  url: string | undefined | null,
  expectedOrigin: string = window.location.origin
): boolean => {
  if (!url) return false;
  try {
    const origin = new URL(expectedOrigin).origin;
    const parsed = new URL(url, origin);
    return parsed.origin === origin;
  } catch {
    return false;
  }
};

export const generateOpenInAppUrl = (
  item: any,
  token: string | null,
  openExperience = false,
  withToken = false,
  pathSuffix = ''
) => {
  const webBaseURL = AppConfig.webUrl;
  const urlWithoutProtocol = webBaseURL.split('https://')[1] ?? webBaseURL.split('http://')[1]; //'192.168.68.64:3000'; // for localhost
  // console.log(webBaseURL, 'webBaseURL', urlWithoutProtocol);
  let expUrl = `assessprep://${urlWithoutProtocol}`;

  if (withToken) {
    // for iOS enforcing login if start in App is clicked
    expUrl += `?token=${token}`;
  }

  if (openExperience) {
    // The appLogin parameter is used to handle the changeSecurity method after a redirect and the changeSecurity method call as per appLogin == true, if macapp open from dynamic config .
    // This is necessary because the `token` will be removed from the query parameters after the redirect.
    // `pathSuffix` lets callers deep-link into a sub-route (e.g. `/local-sync`
    // for APL-11059, so the lockdown app lands directly on the sync screen
    // and the auto-sync effect fires inside the right Dexie partition).
    expUrl = `assessprep://${urlWithoutProtocol}/assessments/${item.uid}${pathSuffix}?firstLoad=true&appLogin=true&token=${token}`;
  }
  // console.log('expUrl===============>>', expUrl);
  return expUrl;
};

export const getTimeRemainingToTargetTime = (targetTime: string, returnType: string) => {
  const currentTime = new Date().getTime();
  const remainingTime = Date.parse(targetTime) - currentTime;

  let convertedTime = null;
  if (returnType === 'seconds') {
    convertedTime = Math.floor(remainingTime / 1000);
  } else if (returnType === 'minutes') {
    convertedTime = Math.floor(remainingTime / 60000);
  } else if (returnType === 'hours') {
    convertedTime = Math.floor(remainingTime / 3600000);
  } else {
    convertedTime = remainingTime;
  }

  return convertedTime;
};

export const currentTimeValidWrtServerTime = (serverTimeOffset: number) => {
  if (Math.abs(serverTimeOffset) > 120000) {
    // 2 mins buffer
    // console.log('checkIfStartTime Please set your system clock to the correct time');
    return false;
  } else {
    return true;
  }
};

export const timeInMiliSeconds = (focusLostTime: number) => {
  // Rename function name
  const date = new Date(focusLostTime);
  const hour = date.getUTCHours();
  const min = date.getUTCMinutes();
  const sec = date.getUTCSeconds();
  const formatedHour = hour > 9 ? hour : '0' + hour;
  const formatedMinute = min > 9 ? min : '0' + min;
  const formatedSecond = sec > 9 ? sec : '0' + sec;
  let timeStamps;
  if (hour > 0) {
    timeStamps =
      hour > 1
        ? formatedHour + ':' + formatedMinute + ':' + formatedSecond + ' hours'
        : formatedHour + ':' + formatedMinute + ':' + formatedSecond + ' hour';
  } else if (min > 0) {
    timeStamps =
      min > 1
        ? formatedMinute + ':' + formatedSecond + ' minutes'
        : formatedMinute + ':' + formatedSecond + ' minute';
  } else {
    timeStamps = sec > 1 ? formatedSecond + ' seconds' : formatedSecond + ' second';
  }
  return timeStamps;
};

export const displayRole = (role: string) => {
  switch (role) {
    case 'superadmin':
      return 'Superadmin';
    case 'admin':
      return 'Admin';
    case 'teacher':
      return 'Teacher';
    case 'student':
      return 'Student';
    default:
      return 'Unknown';
  }
};

export const checkIPAD = () => {
  return (
    /iPad/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) &&
      typeof window.navigator.standalone !== 'undefined' &&
      'ontouchend' in document) ||
    /AssessPrepIOS/.test(navigator.userAgent)
  );
};

export const dropDownTrigger = checkIPAD() || checkMob() ? 'click' : 'hover';

export const checkIfStartTime = (experience: any) => {
  const currentTimeStamp = new Date().getTime(); // in UTC
  const startTime = experience.settings.start_at_datetime;
  const startTillTime = experience.settings.start_till_datetime;
  // in UTC

  // console.log('checkIfStartTime startTime currentTimeStamp', startTime, currentTimeStamp);
  if (!startTime) {
    return true;
  } else {
    const startTimeStamp = Date.parse(startTime);

    // console.log('checkIfStartTime startTimeStamp', startTimeStamp);
    if (startTimeStamp > currentTimeStamp) {
      // not time yet
      return false;
    }
    if (!startTillTime) {
      // can start
      return true;
    }
    const startTillTimeStamp = Date.parse(startTillTime);
    return startTillTimeStamp >= currentTimeStamp;
  }
};
interface RemoveItemFromArrayProps {
  items: any[];
  index: number;
  extra?: {
    minSize?: number;
    noMinSize?: boolean;
  };
}

export const removeItemFromArray = (
  { items, index, extra = {} }: RemoveItemFromArrayProps,
  translate?: any
) => {
  // console.log('removeItemFromArray items, index', items, index);
  const size = items.length;

  let minSize = extra.minSize ?? 1;
  if (extra.noMinSize) {
    minSize = 0;
  }

  index = typeof index == 'undefined' ? size - 1 : index;
  if (minSize && size <= minSize) {
    showInfoModal({
      type: 'warning',
      title: translate('Reached maximum allowed limit') ?? 'Reached maximum allowed limit',
    });
    return items;
  } else {
    items.splice(index, 1);
  }
  return items;
};

export const getDestinationPositionLex = (prevPosition: any, nextPosition?: any) => {
  prevPosition ??= '0';
  // console.log('getDestinationPositionLex prevPosition, nextPosition', prevPosition, nextPosition);
  // *We just need 1 string between the 2
  // *base62 already sets the SymbolTable to use 0-9A-Za-Z
  let destinationPosition = '0';
  try {
    const hexstrings = mudder.base62.mudder(prevPosition, nextPosition); // *Returns an array of strings
    // console.log('getDestinationPositionLex hexstrings', hexstrings);
    destinationPosition = hexstrings[0];
  } catch (error) {
    console.log('getDestinationPositionLex mudder failed', prevPosition, nextPosition);
    message.error(translate('CommonText.failed_to_generate_destination_position'));
  }

  // console.log('final destionaltionPosition==>', destinationPosition);

  return destinationPosition;
};

export const getCanvasSizeObj = (
  e: any,
  handleScroll = false,
  options?: { ignoreElementClassName?: string }
) => {
  const e_width = e.offsetWidth + 15;
  const e_height = e.offsetHeight + 15;
  // x and y values are cropping the text of image, that's why the text is not appearing inside the image in Latest version That's why we gave  x and y to 0.
  const e_x_offset = handleScroll ? window.scrollX : 0;
  const e_y_offset = handleScroll ? window.scrollY : 0;
  const ignoreElementClassName = options?.ignoreElementClassName;
  const sizeObj = {
    scale: 1,
    width: e_width,
    height: e_height,
    x: e_x_offset,
    y: e_y_offset,
    allowTaint: true,
    useCORS: true,
    ignoreElements: (element: Element) => {
      return ignoreElementClassName ? element.classList.contains(ignoreElementClassName) : false;
    },
  };
  return sizeObj;
};

export const removeHTMLTagsFromText = (text: string, onlyTags = false, removeNewlines = true) => {
  if (text) {
    if (onlyTags) {
      text = text.replace(/(<([^>]+)>)/g, '');
      // Remove newlines only if removeNewlines is true. Used in checkGradeWithAI and checkGradeWithAINew for processing answer and question text.
      // We are not removing the new lines because they are needed to present the questions/answers in a readable format when grading other question types.
      if (removeNewlines) {
        text = text.replace(/\n/g, '');
      }
      return text;
    } else {
      return text
        .replace(/(<([^>]+)>)/gi, '')
        .replace(/[\r\s]+/g, '')
        .replace('&nbsp;', '')
        .toLowerCase();
    }
  } else {
    return '';
  }
};

/** DISPLAY-SEAM plain text for authored HTML (syllabus topic labels, tag
 *  labels). Parsed with DOMParser, not a regex, so entities decode the way the
 *  option list's APRenderHtml renders them (`<p>Cells &amp; Ions</p>` reads
 *  "Cells & Ions"). Display only — payloads keep the stored HTML verbatim. */
export const htmlToPlainText = (html: string | null | undefined): string => {
  if (!html) return '';
  // Plain labels (every non-syllabus tag family) skip the parse entirely.
  const text =
    html.includes('<') || html.includes('&')
      ? new DOMParser().parseFromString(html, 'text/html').body.textContent ?? ''
      : html;
  // \s covers the &nbsp; the parser just decoded — a trigger/chip is one line.
  return text.replace(/\s+/g, ' ').trim();
};

export const isStringEmpty = (str: string) => {
  str = removeHTMLTagsFromText(str);
  return !str || (str && str.trim().length === 0);
};

export const getPermissions = (constraints: any, { successCallback, errorCallback }: any) => {
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    navigator.mediaDevices.getUserMedia(constraints).then(successCallback, errorCallback);
  } else {
    // Define getUserMedia with proper type handling
    const getUserMedia =
      (navigator as any).getUserMedia ??
      (navigator as any).webkitGetUserMedia ??
      (navigator as any).mozGetUserMedia;

    if (getUserMedia) {
      getUserMedia.call(navigator, constraints, successCallback, errorCallback);
    } else {
      if (errorCallback) {
        errorCallback(new Error('getUserMedia is not supported'));
      }
    }
  }
};

export const getCurrentPlanStatus = (currentUser: any, options: any = {}) => {
  let isStandardPlan = false;
  let hasMaxOrPlusPlan = false;
  let isAPDPMAXPlan = false;
  let isAPMAXPlan = false;
  const currentUserOrg = currentUser.org ?? {};
  const orgSubscriptions = currentUserOrg.org_subscriptions_attributes ?? [];

  for (const plan of orgSubscriptions) {
    const isPlanTrialOrCustomer = ['Trial', 'Customer'].includes(plan.status);
    if (['APSTD'].includes(plan.code) && isPlanTrialOrCustomer) {
      isStandardPlan = true;
    }

    if (['APMAX', 'APPLUS'].includes(plan.code) && isPlanTrialOrCustomer) {
      hasMaxOrPlusPlan = true;
    }

    if (['APDPMAX'].includes(plan.code) && isPlanTrialOrCustomer) {
      isAPDPMAXPlan = true;
    }

    if (['APMAX'].includes(plan.code) && isPlanTrialOrCustomer) {
      isAPMAXPlan = true;
    }
  }

  const isNonDPProgramWithOnlyDPMaxPlan =
    isAPDPMAXPlan && !isAPMAXPlan && options.programmeLabel !== 'IB Diploma';

  return {
    isStandardPlan,
    hasMaxOrPlusPlan,
    isNonDPProgramWithOnlyDPMaxPlan,
  };
};

export const checkParentElementHasClassNames = (element: any, classNames: any): boolean => {
  const parentElement = element.parentElement;
  if (parentElement) {
    const classList = parentElement.classList;
    for (const className of classNames) {
      if (classList.contains(className)) {
        return true;
      }
    }
    return checkParentElementHasClassNames(parentElement, classNames);
  }
  return false;
};

export const renderMathInElement = async (element: any, renderUsingWiris = true) => {
  const win = window as any;
  const MathJax = win.MathJax;

  if (element) {
    if (MathJax && MathJax.typeset) {
      setTimeout(() => {
        MathJax.typeset([element]);
      }, 10);
      // }, renderUsingWiris ? 2000 : 10)
    }
    // doing this to render Mathjax style only in case of MB
    if (renderUsingWiris) {
      const wirisViewer = win.com?.wiris?.js?.JsPluginViewer;
      if (wirisViewer) {
        wirisViewer.parseElement(element, true, function () {
          // Callback after parsing is complete
          // console.log('wirisViewer.parseElement callback');
        });
      }
    }
  }
};

export const availableLanguageOptionsForAI = [
  'English',
  'French',
  'Spanish',
  'German',
  'Korean',
  'Hindi',
  'Japanese',
  'Chinese',
  'Greek',
  'Arabic',
  'Russian',
  'Norwegian',
  'Portuguese',
  'Dutch',
  'Italian',
  'Swedish',
  'Polish',
  'Turkish',
  'Thai',
  'Indonesian',
  'Vietnamese',
  'Malay',
  'Danish',
  'Finnish',
  'Urdu',
  'Swahili',
  'Ukrainian',
];

export const fetchWithStream = (options: any) => {
  const {
    url,
    dataToPost,
    setLoading,
    setError,
    onTextChanged,
    setTriggerAPDataUpdate,
    successCallback,
    sseRef,
    removeVerticalCursor,
    expectedResponseFormat,
    errorCallback,
  } = options;
  let { content } = options;
  const token = useAuthStore.getState().token;
  let config = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    meta: { dataProviderName: 'aiService' },
  };
  config = updateConfigHeaderForDemoUseStagingAI(config);
  const abortController = new AbortController();
  if (sseRef) {
    sseRef.current = {
      abortSse: () => {
        abortController.abort();
      },
    };
  }

  let time = 0;
  const timer = setInterval(() => {
    time += 1;
    // console.log('time==>', time);
  }, 1000);
  fetchEventSource(url, {
    method: 'POST',
    ...config,
    body: JSON.stringify({
      stream: true,
      ...dataToPost,
    }),
    signal: abortController.signal,
    onmessage: (event: any) => {
      if (abortController.signal.aborted) {
        // Abort signal received, stop further processing
        return;
      }
      setLoading?.(false);
      const data = JSON.parse(event.data);
      // console.log('sonnet data==>', event);
      // return;
      let finishReason = 'continue';
      let updatedContent;

      const isOpenAI = Object.prototype.hasOwnProperty.call(data, 'choices');
      if (isOpenAI) {
        const dataChoices = data.choices?.[0];
        const deltaContent = dataChoices.delta?.content;
        if (deltaContent || dataChoices.finish_reason === 'stop' || data.done) {
          if (deltaContent) {
            content += deltaContent;
          }

          if (dataChoices.finish_reason === 'stop' || data.done) {
            updatedContent = content;
            finishReason = 'stop';
          } else if (removeVerticalCursor) {
            updatedContent = content;
          } else {
            updatedContent =
              content +
              "<span style='width: 10px; height: 15px; background-color: black; animation: blink 0.5s infinite;'>|</span>";
          }
        }

        // onTextChanged && onTextChanged(content)
        // onTextChanged && onTextChanged(updatedContent)
      } else if (data.type === 'content_block_delta') {
        content += data.delta.text;
        if (removeVerticalCursor) {
          updatedContent = content;
        } else {
          updatedContent =
            content +
            "<span style='width: 10px; height: 15px; background-color: black; animation: blink 0.5s infinite;'>|</span>";
        }
      } else if (data.type === 'response.output_text.delta') {
        // Handle the new delta format
        content += data.delta;
        if (removeVerticalCursor) {
          updatedContent = content;
        } else {
          updatedContent =
            content +
            "<span style='width: 10px; height: 15px; background-color: black; animation: blink 0.5s infinite;'>|</span>";
        }
      } else {
        updatedContent = content;
        finishReason = 'stop';
      }
      if (successCallback) {
        if (expectedResponseFormat === 'json') {
          if (finishReason === 'stop') {
            try {
              const parsedData = JSON.parse(updatedContent);
              clearInterval(timer);
              successCallback(parsedData, finishReason);
            } catch (error) {
              console.log('generate-segments stream data best effort parsed error ======>', error);
              clearInterval(timer);
              setLoading?.(false);
              setError?.('Something went wrong. Please try again!');
            }
          } else if (time % 2 === 0) {
            console.log('updatedContent =====>', updatedContent);
            try {
              const parsedData = parse(updatedContent);
              successCallback(parsedData, finishReason);
            } catch (error) {
              console.log('generate-segments stream data best effort parsed error ======>', error);
            }
          }
        } else {
          // normal text
          successCallback(updatedContent, finishReason);
        }
      }
      setTriggerAPDataUpdate?.(getUniqueId());
    },
    onerror(err: any) {
      if (abortController.signal.aborted) {
        // Abort signal received, stop further processing
        return;
      }
      clearInterval(timer);
      setLoading?.(false);
      errorCallback?.();
      setError?.('Something went wrong. Please try again!');
      throw err;
    },
  });
};

export const fetchWithoutStream = async (options: any) => {
  const {
    url,
    dataToPost,
    setLoading,
    setError,
    successCallback,
    expectedResponseFormat,
    errorCallback,
    abortControllerRef,
  } = options;

  const token = useAuthStore.getState().token;
  let config = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    meta: { dataProviderName: 'aiService' },
  };
  config = updateConfigHeaderForDemoUseStagingAI(config);

  const abortController = new AbortController();

  // Store abort controller reference if provided
  if (abortControllerRef) {
    abortControllerRef.current = {
      abort: () => {
        abortController.abort();
      },
    };
  }

  try {
    setLoading?.(true);

    const response = await fetch(url, {
      method: 'POST',
      headers: config.headers,
      body: JSON.stringify({
        stream: false,
        ...dataToPost,
      }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    setLoading?.(false);

    // Handle different response formats (OpenAI vs Anthropic vs other)
    if (successCallback) {
      successCallback(data, 'stop');
    }

    return data;
  } catch (error: any) {
    // Don't show error if request was aborted
    if (error.name === 'AbortError') {
      console.log('Request aborted');
      setLoading?.(false);
      return;
    }

    setLoading?.(false);
    errorCallback?.();
    setError?.('Something went wrong. Please try again!');
    console.error('fetchWithoutStream error:', error);
  }
};

export const getPrintOptionTitle = (key: string, translate: any, printOptionsToSend?: any) => {
  // console.log(key, 'key printOptionstosend');
  const printOptionsTitle = {
    // with_cover_page: translate('ExportModal.include_cover_page'),
    with_questions: translate('CommonText.questions'),
    with_answer_explanation: translate('ExportModal.teachers_explanations'),
    with_student_responses: translate('ExportModal.student_responses'),
    with_space_for_answers: translate('ExportModal.space_for_student_answers'),
    with_markscheme: translate('ExportModal.markschemes'),
    with_tsc: translate('CommonText.task_specific_clarification'),
    with_print_copies_for_each_student: translate(
      'ExportModal.print_copies_respective_to_each_student'
    ),
    with_print_copies_for_each_student_with_combine_pdf: translate(
      'ExportModal.with_print_copies_for_each_student_with_combine_pdf'
    ),
    with_blank_space_for_student_name: translate('ExportModal.print_with_blank_space_for_name'),
  };
  const returnValue = printOptionsTitle[key as keyof typeof printOptionsTitle];
  console.log(key, 'key printOptionstosend', returnValue);
  return returnValue;
};

export const getFormattedPrintOptionsTitle = (arr: string[]) => {
  const arrLength = arr.length;
  if (arrLength === 0) {
    return '';
  } else if (arrLength === 1) {
    return arr[0];
  } else {
    const lastOption = arr.pop();
    return arr.join(', ') + ' and ' + lastOption;
  }
};

export const encodeURLCustom = (url: string, exportType: string) => {
  const splitArr = url.split('/');
  const fileName = splitArr.pop();
  let finalURL = url;
  if (fileName && exportType !== 'all_student_grades' && exportType !== 'analytics') {
    // all_student_grades excel url is from BE rails - already encoded
    finalURL = url.replace(fileName, '') + encodeURIComponent(fileName).toString();
  }
  console.log('exportModalConfig.exportUrl', encodeURIComponent(url), finalURL);
  return finalURL;
};

export const getQBIcon = (code: string) => {
  const qbIconMap: any = {
    ib_dp: IBLogoOSC,
    osc_dp: PamojaIcon,
  };
  // AssessPrep-owned banks (global rows, `ap_` code prefix) get the brand mark;
  // school/district-created banks keep the letter-avatar fallback.
  return qbIconMap[code] ?? (code?.startsWith('ap_') ? LogoImage : undefined);
};

export const programmesWithCustomSubjects = ['hs', 'ms', 'ps', 'fs'];

export const b64toBlob = (base64String: string, sliceSize?: number) => {
  const block = base64String.split(';');
  let contentType = block[0].split(':')[1];
  const b64Data = block[1].split(',')[1];

  // console.log("block, contentType, b64Data", block, contentType, b64Data)
  contentType = contentType ?? '';
  sliceSize = sliceSize ?? 512;

  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};
export const getExperienceUrl = (
  data: any,
  isStudent: boolean,
  appType: string,
  tab = 'default',
  currentUser: any = null,
  event?: React.MouseEvent,
  fromResourceName?: string
) => {
  // Check if experience is archived and return empty url
  if (data.archived && !isStudent) {
    return {
      url: '',
      shouldNavigate: false,
      openInNewTab: false,
      preventDefault: false,
    };
  }
  // // Check if offline experience is not allowed on desktop/mobile apps
  // if (data.settings.mode === 'offline' && (appType === 'mac' || appType === 'ios')) {
  //   return {
  //     url: '',
  //     shouldNavigate: false,
  //     openInNewTab: false,
  //     preventDefault: false,
  //   };
  // }

  const baseUrl = `/assessments/${data.uid}`;
  let url = baseUrl;
  const shouldNavigate = true;
  let openInNewTab = false;
  let preventDefault = false;

  // Handle different tabs
  if (tab === 'monitor') {
    url = `${baseUrl}/monitor`;
    openInNewTab = true; // Always new tab for monitor
  } else if (tab === 'grade') {
    url = `${baseUrl}/grade`;
    // Grade tab logic
    if (
      currentUser?.role === 'qb_author' ||
      currentUser?.role === 'superadmin' ||
      currentUser?.role === 'support'
    ) {
      openInNewTab = true; // Admin roles open in new tab
    }
  } else if (tab === 'create') {
    url = `${baseUrl}/create`;
  } else {
    // Default tab logic
    if (currentUser?.role === 'superadmin' || currentUser?.role === 'support') {
      openInNewTab = true; // Admin roles default to new tab
    } else if (event && (event.ctrlKey || event.metaKey)) {
      // Handle Ctrl+Click for new tab
      if (currentUser?.role === 'student') {
        preventDefault = true; // Students always stay in same tab
      } else {
        openInNewTab = true; // Others can open in new tab with Ctrl+Click
        url = fromResourceName ? `${url}/${fromResourceName}` : url;
      }
    }
  }

  return {
    url,
    shouldNavigate,
    openInNewTab,
    preventDefault,
  };
};

export const StatusTypes = [
  {
    label: 'Published',
    value: 'published',
  },
  {
    label: 'Draft',
    value: 'draft',
  },
];

export const powerUserSelectOptions = [
  { label: 'Power authors (assessments delivered)', value: 'power_authors_delivered' },
  { label: 'Power authors (original items)', value: 'power_authors_items' },
  { label: 'Power students (most submissions)', value: 'power_students_submissions' },
];
export const getExperienceAdditionalInfo = (data: any, isStudent: boolean) => {
  const experienceSettings = data.settings;
  const evaluationType = experienceSettings?.evaluation_type;
  const subjectLabel = data.custom_fields?.subject_item_label;
  const baseInfo = [
    {
      key: 'subject',
      value: subjectLabel,
      icon:
        subjectLabel && getSubjectIconMap()[subjectLabel.toLowerCase()]
          ? `https://v2res1.assessprep.com/manual_uploads/icons/sebo_icons/${
              getSubjectIconMap()[subjectLabel.toLowerCase()]
            }`
          : undefined,
    },
    {
      key: 'grade',
      value: data.custom_fields?.grade_item_label,
    },
  ];

  if (!isStudent) {
    const rubricTitle = data?.rubric?.title ?? '';
    const hidePoints = RUBRIC_TYPES_WITHOUT_POINTS.includes(data?.rubric?.type_c);
    const categoryItems = data.relation_items?.[0]?.category_items ?? [];
    const categoryItem = categoryItems?.filter((item: any) => item.archived === false)?.[0];
    const category = categoryItem?.label;

    const infoToAdd: any[] = [];
    if (rubricTitle !== 'Points') {
      infoToAdd.push({
        key: 'rubric',
        value: rubricTitle ?? ' - ',
      });
    }

    if (evaluationType) {
      const evaluationTypeValue = toTitleCase(evaluationType);
      // Skip adding "Points" as evaluation type if we're also showing points value
      // Only show evaluation type if it's not "Points" AND points are not hidden
      const isPointsEvaluationType = evaluationTypeValue?.toLowerCase() === 'points';
      // Don't add evaluation type if it's "Points" and we're showing points
      if (!(isPointsEvaluationType && !hidePoints)) {
        infoToAdd.push({
          key: 'evaluation_type',
          value: evaluationTypeValue ?? '',
        });
      }
    }

    if (!hidePoints) {
      const pointsNum = Number(data.points || 0);
      infoToAdd.push({
        key: 'points',
        value: `${pointsNum.toLocaleString()} ${pointsNum === 1 ? 'Pt' : 'Pts'}`,
      });
    }

    if (category) {
      infoToAdd.push({
        key: 'category',
        value: category,
        shouldTruncate: true,
      });
    }

    baseInfo.push(...infoToAdd);
  }

  return baseInfo;
};

export const getExperienceDaysAgo = (date: string, translate: any) => {
  const daysAgo = Math.floor(
    (new Date().setHours(0, 0, 0, 0) - new Date(date).setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24)
  );

  if (daysAgo === 0) {
    return translate('CommonText.today');
  }

  if (daysAgo < 7) {
    return `${daysAgo}d ago`;
  }

  if (daysAgo < 30) {
    return `${Math.floor(daysAgo / 7)}w ago`;
  }

  if (daysAgo < 365) {
    return `${Math.floor(daysAgo / 30)}mon ago`;
  }

  return `${Math.floor(daysAgo / 365)}y ago`;
};

export const getExperienceDeliveryModeObject = (mode: any) => {
  switch (mode) {
    case 'online':
      return { label: 'Browser', icon: CloudLinear };
    case 'online_secure':
      return { label: 'Browser (Security)', icon: ShieldCheckLinear };
    case 'offline':
      return { label: 'Lockdown app', icon: LockLinear };
    case 'paper':
      return { label: 'Paper', icon: DocumentAddLinear };
    default:
      return { label: '-', icon: null };
  }
};

export const getCellClass = (
  points: number | null | undefined,
  attempted: boolean,
  graded: boolean
) => {
  if (!attempted) return 'cell-border-dashed-red';
  if (points === null || points === undefined) return 'cell-bg-gray';
  if (!graded) return 'cell-bg-gray cell-border-gray';
  if (points >= 75) return 'cell-border-blue';
  if (points >= 51) return 'cell-border-green';
  if (points >= 34) return 'cell-border-yellow';
  if (points >= 0) return 'cell-border-red';
  return '';
};

export const setUserInfoOnBeforeEndSession = (actionFrom: any, setLogs: any, appType: any) => {
  // On the native side, in the event of 'Session End.' taping on this option triggers a function to save the log information.
  const actionType = appType == 'mac' ? 'mac_native_quit' : 'lockdown_disabled';
  let lockdownDisableAction = '';
  switch (actionFrom) {
    // TODO: separate components for each view
    case 'joined':
      lockdownDisableAction = `${actionType}_from_join_view`;
      break;

    case 'startTest':
      lockdownDisableAction = `${actionType}_from_start_test`;
      break;

    case 'resumeTest':
      lockdownDisableAction = `${actionType}_from_resume_view`;
      break;

    case 'endTest':
      lockdownDisableAction = `${actionType}_from_end_view`;
      break;

    case 'submittedTest':
      lockdownDisableAction = `${actionType}_from_submit_view`;
      break;

    case 'errorInPage':
      lockdownDisableAction = `${actionType}_while_error_in_page`;
      break;

    default:
      lockdownDisableAction = `${actionType}`;
      break;
  }

  setLogs({
    logging: true,
    action: lockdownDisableAction,
  });
};

export const forceHideAnnotation = () => {
  const annotationPopup = document.querySelectorAll('.annotator-viewer');
  if (annotationPopup) {
    annotationPopup.forEach((popup) => {
      popup.classList.add('annotator-hide');
    });
  }
};

export const getScreenshot = (e: any, callback: any) => {
  if (e) {
    toPng(e, {
      width: e.offsetWidth,
      height: e.offsetHeight,
      pixelRatio: 1,
    }).then((dataUrl: string) => {
      callback(dataUrl);
    });
  }
};

export const setQueryParams = (options: any) => {
  const { url, history, location, newParams, hardReload } = options;
  let { removeParams } = options;
  const updatedNewParams: any = {};
  for (const key in newParams) {
    if (Object.prototype.hasOwnProperty.call(newParams, key)) {
      const element = newParams[key];
      if (newParams[key] === null || newParams[key] === 'null') {
        removeParams ??= [];
        removeParams.push(key);
      } else {
        updatedNewParams[key] = element;
      }
    }
  }
  const queryParams = {
    ...queryString.parse(location.search),
    ...updatedNewParams,
  };

  if (removeParams) {
    removeParams.forEach((element: string) => {
      if (Object.prototype.hasOwnProperty.call(queryParams, element)) {
        delete queryParams[element as keyof typeof queryParams];
      }
    });
  }
  const finalQueryParams = queryString.stringify(queryParams);
  const newUrl = `${url}${finalQueryParams ? `?${finalQueryParams}` : ''}`;
  // We have to do hard reload as Grade Table syncWithLocation gets back initial params

  if (hardReload) {
    window.location.href = newUrl;
  } else {
    if (typeof history.replace === 'function') {
      history.replace(`${url}?${finalQueryParams}`);
    } else if (typeof history.replaceState === 'function') {
      history.replaceState(null, '', `${url}?${finalQueryParams}`);
    } else {
      window.location.replace(`${url}?${finalQueryParams}`);
    }
  }
};

export const convertToNumberArray = (arr: any) => {
  return arr.map((item: any) => Number(item));
};

export const insertAndDeleteAtArrayPath = (
  obj: any,
  path: (string | number)[],
  index: number,
  value: any,
  shouldArchive?: boolean
) => {
  // Get the array at the specified path
  const targetArray = get(obj, path);
  // console.log('targetArray check =====>', targetArray, path, index, value, obj);
  if (Array.isArray(targetArray)) {
    // Insert the value at the specified index
    if (shouldArchive) {
      targetArray.splice(index, 1);
    } else {
      targetArray.splice(index, 0, value);
    }
    // Update the array at the path
    set(obj, path, targetArray);
  } else {
    console.warn(`Target at path ${path} is not an array`);
  }
};

export const updateAtArrayPath = (obj: any, path: (string | number)[], value: any) => {
  set(obj, path, value);
};

export const updateFiltersFromFormValues = (filters: any, formValues: any) => {
  // Remove filters with fields present in formValues
  const updatedFilters = filters.filter(
    (filter: any) => !Object.keys(formValues).includes(filter.field)
  );

  // Always add new filters for each key in formValues, even if value is null or undefined
  Object.entries(formValues).forEach(([field, value]) => {
    updatedFilters.push({ field, operator: 'eq', value });
  });

  return updatedFilters;
};

export const getDefaultFilterValue = (fieldName: string, filters: any[], options: any = {}) => {
  const { valueType = 'string' } = options;
  const defaultFilterValue = getDefaultFilter(fieldName, filters);
  if (defaultFilterValue) {
    if (valueType === 'number_array') {
      return defaultFilterValue.map((v: any) => Number(v));
    } else if (valueType === 'number') {
      return Number(defaultFilterValue);
    } else if (valueType === 'boolean') {
      return defaultFilterValue === 'true' || defaultFilterValue === true;
    } else {
      return defaultFilterValue;
    }
  }
  return undefined;
};

// Parse a value (e.g. an AntD Select's onChange argument) into a number, or null
// when empty / non-numeric (clear, deselect, or an unexpected string).
export const toNumberOrNull = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const focusElement = (selector: string, delay = 0, retries = 2) => {
  const tryFocus = (attempt = 0) => {
    const element = document.querySelector(selector) as HTMLElement;

    // Element not found - retry if attempts left
    if (!element) {
      return attempt < retries ? setTimeout(() => tryFocus(attempt + 1), 100) : null;
    }

    // Element not ready - retry if attempts left
    const isReady =
      element.offsetWidth > 0 &&
      element.offsetHeight > 0 &&
      !element.hasAttribute('disabled') &&
      !element.hasAttribute('readonly');

    if (!isReady) {
      return attempt < retries ? setTimeout(() => tryFocus(attempt + 1), 100) : null;
    }

    // Focus the element
    try {
      element.focus();
      // Verify focus worked
      if (document.activeElement === element) {
        return true; // Success
      }
    } catch (e) {
      // Silent fail, will retry if attempts left
    }

    // Focus failed - retry if attempts left
    return attempt < retries ? setTimeout(() => tryFocus(attempt + 1), 100) : null;
  };

  // Start focusing with optional delay
  return delay > 0 ? setTimeout(() => tryFocus(), delay) : tryFocus();
};

// Quick focus helper for immediate use
export const quickFocus = (selector: string) => focusElement(selector, 0, 1);

/** 'auto' under prefers-reduced-motion, 'smooth' otherwise (take surface's
 *  pageScrollBehavior pattern, shared for form scroll-to-error). */
export const formScrollBehavior = (): ScrollBehavior =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';

/**
 * P2-76: on a failed submit-validation, a field error rendered off-screen
 * left Save looking dead. Scrolls the first invalid field into view and
 * focuses it.
 *
 * - `fieldEl` — caller already holds a live ref to the exact control (e.g. a
 *   shell-owned input): scrolled/focused synchronously, no DOM race.
 * - Omitted `fieldEl` — generic RHF fallback: searches `container` for the
 *   first `[data-slot="form-message"]` error node (shadcn's FormMessage
 *   marker, also applied by hand on segmentForm's non-shadcn field errors).
 *   Deferred one tick since RHF's error-state re-render hasn't committed yet
 *   when the submit-rejection handler runs.
 */
export const scrollToFirstFormError = (
  fieldEl?: HTMLElement | null,
  container: ParentNode = document
): void => {
  const run = () => {
    const target = fieldEl ?? container.querySelector<HTMLElement>('[data-slot="form-message"]');
    if (!target) return;
    target.scrollIntoView({ behavior: formScrollBehavior(), block: 'center' });
    if (target === fieldEl) {
      target.focus();
      return;
    }
    // Error text isn't natively focusable — make it a programmatic focus
    // target (WAI-ARIA "focus the message" pattern) without joining tab order.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };
  if (fieldEl) {
    run();
  } else {
    setTimeout(run, 100);
  }
};

export const shouldHideQuestionsOrOptionsText = (printMode: boolean, printOptions: any) => {
  if (
    printMode &&
    (printOptions.print_type === 'experience' ||
      printOptions.print_type === 'experience_student_pack')
  ) {
    return printOptions.with_space_for_answers && !printOptions.with_questions;
  }

  return false;
};

/**
 * Scrolls smoothly to a question element by its ID within the segment list container.
 * @param questionId - The unique identifier of the question element.
 */
export const scrollToSegment = (questionId: number | string): void => {
  const container = document.querySelector('.segment-list-container') as HTMLElement | null;
  if (!container) return;

  const element = container.querySelector<HTMLElement>(`#item-${questionId}`);
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
};

/**
 * Clears all mapping-related properties from a file/image object
 * @param fileObject - The file object to clean
 * @returns The same object with mapping properties removed
 */
export const clearFileMappingProperties = (fileObject: any): any => {
  const propertiesToDelete = [
    'segment_ids',
    'segment_id',
    'segment_index_path',
    'segment_index_paths', // plural version used in some contexts
    'topic_id',
    'topic_index',
    'first_file',
    'ai_mapped',
    'user_id',
  ];

  propertiesToDelete.forEach((prop) => {
    delete fileObject[prop];
  });

  return fileObject;
};

export const isPaperModeAndMcqOnly = (experience: any) => {
  const isPaperMode = experience?.settings?.mode === 'paper';
  const containsMcqsOnly = experience?.contains_mcqs_only ?? false;

  return isPaperMode && containsMcqsOnly;
};

export const experienceNameWithAccessCode = (experience: any) => {
  return `${experience.name} (${experience.access_code})`;
};

export const handleClassAndUserFilters = (options: any) => {
  const {
    filters,
    setFilters,
    updatedFilterValues,
    setFieldsValue,
    bulkArchiveData,
    setBulkArchiveData,
  } = options;

  setFieldsValue(updatedFilterValues);
  const updatedFilters = updateFiltersFromFormValues(filters, updatedFilterValues);
  setBulkArchiveData(bulkArchiveData);
  setFilters(updatedFilters);
};

export const getDateTimeFormat = (dateString: string, customFormat = 'MMM DD, YYYY HH:mm A') => {
  if (!dateString) return '-';
  return dayjs(dateString).format(customFormat);
};

export const createIndexPath = (
  parentIdxs: number[],
  shouldAddAttributesAtFirstLevel = true
): (string | number)[] => {
  if (!parentIdxs || parentIdxs.length === 0) {
    return [];
  }

  const path: (string | number)[] = [];

  // Add first index with attributes
  if (shouldAddAttributesAtFirstLevel) {
    path.push(parentIdxs[0], 'attributes');
  } else {
    path.push(parentIdxs[0]);
  }

  // Add nested path for each subsequent level
  for (let i = 1; i < parentIdxs.length; i++) {
    path.push('nested_children', parentIdxs[i]);
  }

  return path;
};

export function getAllSegmentsChainContext(
  segmentData: any,
  parentSegmentIdxs: number[],
  currentQuestionContent: string,
  addQuestionPrefix = true
) {
  let concatenatedContent = '';

  for (let levelIndex = 0; levelIndex < parentSegmentIdxs.length; levelIndex++) {
    const targetSegmentIndexAtLevel = parentSegmentIdxs[levelIndex];
    const shouldAddAttributesAtFirstLevel = false;

    // At each level, traverse from 0 to targetSegmentIndexAtLevel
    for (let segmentIndex = 0; segmentIndex <= targetSegmentIndexAtLevel; segmentIndex++) {
      // Build path to segment at current level by reusing createIndexPath pattern
      const indicesUpToCurrentLevel = parentSegmentIdxs.slice(0, levelIndex).concat(segmentIndex);

      const segmentPathAtLevel = createIndexPath(
        indicesUpToCurrentLevel,
        shouldAddAttributesAtFirstLevel
      );

      const segment = get(segmentData, segmentPathAtLevel, null);
      console.log('segment ==>', segment, segmentPathAtLevel);

      const currentSegmentIndex = parentSegmentIdxs.length - 1;
      const isCurrentQuestion =
        levelIndex === currentSegmentIndex && segmentIndex === targetSegmentIndexAtLevel;
      if (isCurrentQuestion) {
        const currentPrefix = addQuestionPrefix ? 'Question: ' : '';
        concatenatedContent += `\n ${currentPrefix}${currentQuestionContent}`;
        break;
      }

      if (!segment) break;

      // Process only image/pdf resource segments
      if (segment.segment_type === 'resource') {
        const resourceType = segment.resource_segment_attributes?.resource_type;
        if (['image', 'pdf'].includes(resourceType)) {
          const resourceAttributes = segment.resource_segment_attributes;
          resourceAttributes.resource_json?.segment_data?.attachments?.forEach(
            (attachment: any) => {
              concatenatedContent += `\n Resource ${resourceType}: ${attachment.url}`;
            }
          );
        }
      } else if (segment.segment_type === 'text') {
        concatenatedContent += `\n Paragraph: ${segment.text_segment_attributes.content}`;
      } else if (segment.segment_type === 'question') {
        if (
          segment.question_segment_attributes.question_type === 'group' ||
          segmentIndex === targetSegmentIndexAtLevel
        ) {
          const questionContent = segment.question_segment_attributes.content;
          const parentPrefix = addQuestionPrefix ? 'Question: ' : '';
          concatenatedContent += `\n ${parentPrefix}${questionContent}`;
        }
      }
    }
  }

  return concatenatedContent;
}
export const getWindowSafeExamBrowser = () => {
  return (window as any).SafeExamBrowser;
};

export const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

/**
 * Calculates the number of print lines for answer space based on rubric type and segment data
 *
 * @param options - Configuration object
 * @param options.printLinesCount - Pre-set print lines count (if provided, returns this value)
 * @param options.rubricType - Type of rubric: 'myp_achievement_level', 'criteria_with_points', 'comments_only', or undefined
 * @param options.criteriumAssociationsAttributes - Array of criteria associations (for AL and CWP types)
 * @param options.points - Points value (for simple points calculation)
 * @returns Calculated number of print lines, or undefined if calculation cannot be performed
 */
export const calculatePrintLinesForAnswerSpace = (options: {
  printLinesCount?: string | number | null;
  rubricType?: string;
  criteriumAssociationsAttributes?: any[];
  points?: number | string | null;
  answerSpaceMultiplier?: number;
}): number | undefined => {
  const {
    printLinesCount,
    rubricType,
    criteriumAssociationsAttributes,
    points,
    answerSpaceMultiplier,
  } = options;
  const defaultPrintLinesCount = 2;
  const multiplier = answerSpaceMultiplier ?? 2;
  // Helper to cap result at MAX_PRINT_LINES_COUNT
  const capResult = (value: number) => Math.min(value, MAX_PRINT_LINES_COUNT);

  // If print lines count is explicitly set, use it
  if (printLinesCount !== undefined && printLinesCount !== null) {
    return capResult(Number(printLinesCount));
  }

  // Helper to check if criteria is selected
  const isCriteriaSelected = (criteria: any) =>
    criteria.checked || (typeof criteria.checked === 'undefined' && criteria.id);

  // AL (Achievement Level): checked_count × 4
  if (rubricType === 'myp_achievement_level') {
    if (Array.isArray(criteriumAssociationsAttributes)) {
      const checkedCount = criteriumAssociationsAttributes.filter(isCriteriaSelected).length;
      // If no checked items, use length as fallback (for SegmentItem.tsx case)
      return capResult(checkedCount * 2 * multiplier);
    }
  }

  // CWP (Criteria With Points): sum of points from selected criteria × 2
  if (rubricType === 'criteria_with_points') {
    if (Array.isArray(criteriumAssociationsAttributes)) {
      let totalPoints = 0;

      for (const criteria of criteriumAssociationsAttributes) {
        if (isCriteriaSelected(criteria) && criteria.points) {
          totalPoints += parseFloat(criteria.points);
        }
      }

      if (totalPoints > 0) {
        return capResult(totalPoints < 1 ? 1 : Math.floor(totalPoints * multiplier));
      }
    }
    return defaultPrintLinesCount;
  }

  // Comments Only: default 4 lines
  if (rubricType === 'comments_only') {
    return 4;
  }

  // Simple points: points × 2 (minimum 1)
  if (points) {
    return capResult(Number(points) < 1 ? 1 : Math.floor(Number(points) * multiplier));
  }

  return defaultPrintLinesCount;
};
